import type { AxiosError } from 'axios'
import translateService from '@widgets/shared/services/translate/translateService'
import type { Ref } from '@/composition/vue/compositionApi'
import { reactive, ref } from '@/composition/vue/compositionApi'
import type { Dict } from '@/types'
import type { InternalErrorResponse } from '@/services/transport/types'
import { useProvideInject } from '@/composition/provideInject'

export type WidgetError = string[]

export type WidgetFieldValidate<F, V> = (value: V, form: F) => WidgetError

export type WidgetFieldConfig<F, V> = {
  value: V
  validate?: WidgetFieldValidate<F, V>
  disabled?: boolean
}

export type WidgetFieldsConfig<F> = {
  [K in keyof F]: WidgetFieldConfig<F, F[K]>
}

export type WidgetErrors<F> = {
  [K in keyof F]: WidgetError
}

export type WidgetFormConfig = {
  readonly?: boolean
}

export type WidgetFormSharedProps<F extends Dict> = {
  form: F
  errors: WidgetErrors<F>
  commonErrors: Ref<string[]>
  config: WidgetFormConfig
  setFieldValue: <K extends keyof F>(fieldName: K, value: F[K]) => void
  setServerErrors: (serverErrors: Dict<string[]>) => void
  validateField: (fieldName: keyof F) => boolean
  validateForm: () => boolean
  submit: () => Promise<boolean>
}

export type CastCommonErrorToMessagesCallback = (
  status?: number,
  errorResponse?: InternalErrorResponse,
) => WidgetError | undefined

const widgetFormPropsSymbol = Symbol('widgetForm')
export const useWidgetFormProvideInject = <F extends Dict>() =>
  useProvideInject<Partial<WidgetFormSharedProps<F>>>(widgetFormPropsSymbol)

export const useWidgetForm = <F extends Dict>(
  fieldsConfig: WidgetFieldsConfig<F>,
  submitCallback: (formData: F) => Promise<void>,
  initialFormConfig: WidgetFormConfig = {},
  castCommonErrorToMessagesCallback?: CastCommonErrorToMessagesCallback,
): WidgetFormSharedProps<F> => {
  const getValuesFromFieldConfig = () =>
    Object.fromEntries(Object.entries(fieldsConfig).map(([key, { value }]) => [key, value])) as F

  const getErrorsFromFieldConfig = () =>
    Object.fromEntries(Object.entries(fieldsConfig).map(([key]) => [key, []])) as unknown as WidgetErrors<F>

  const config = reactive<WidgetFormConfig>(initialFormConfig)

  const form = reactive(getValuesFromFieldConfig()) as F

  const errors = reactive(getErrorsFromFieldConfig()) as WidgetErrors<F>

  const commonErrors = ref<WidgetError>([])

  const patchFormConfig = (patch: Partial<WidgetFormConfig>) => {
    Object.assign(config, patch)
  }

  const setFieldValue: WidgetFormSharedProps<F>['setFieldValue'] = (fieldName, value) => {
    form[fieldName] = value
  }

  const setServerErrors: WidgetFormSharedProps<F>['setServerErrors'] = (serverErrors) => {
    Object.keys(fieldsConfig).forEach((fieldName: keyof F) => {
      const fieldErrors = serverErrors[fieldName as string]
      if (fieldErrors?.length) {
        errors[fieldName] = fieldErrors
      }
    })
  }

  const setCommonServerErrors = (commonServerErrors: string[]) => {
    commonErrors.value = commonServerErrors
  }

  const validateField: WidgetFormSharedProps<F>['validateField'] = (fieldName) => {
    const fieldConfig = fieldsConfig[fieldName]
    errors[fieldName] = fieldConfig.disabled ? [] : fieldConfig.validate?.(form[fieldName], form) || []
    return !errors[fieldName].length
  }

  const validateForm = () => {
    Object.keys(fieldsConfig).forEach((fieldName) => validateField(fieldName as keyof F))
    return !Object.values(errors).find((value) => value.length)
  }

  const getSubmitData = (): F =>
    Object.entries(fieldsConfig).reduce((formData, [fieldName, { disabled }]) => {
      if (!disabled) {
        formData[fieldName] = form[fieldName]
      }
      return formData
    }, {}) as F

  const submit = async (): Promise<boolean> => {
    if (config.readonly) return false
    try {
      setCommonServerErrors([])
      if (!validateForm()) return false
      patchFormConfig({ readonly: true })
      const formData = getSubmitData()
      await submitCallback(formData)
      return true
    } catch (e) {
      const response = (e as AxiosError<InternalErrorResponse>).response
      const errorData = response?.data

      const serverErrors = errorData?.errors?.fields as Dict<string[]>
      if (serverErrors) {
        setServerErrors(serverErrors)
      }

      const commonErrorMessages =
        castCommonErrorToMessagesCallback?.(response?.status, errorData) ?? castDefaultCommonErrorMessages(errorData)
      setCommonServerErrors(commonErrorMessages)

      return false
    } finally {
      patchFormConfig({ readonly: false })
    }
  }

  const widgetFormProps = {
    form,
    errors,
    commonErrors,
    config,
    patchFormConfig,
    setFieldValue,
    setServerErrors,
    validateField,
    validateForm,
    submit,
  }
  useWidgetFormProvideInject<F>().provide(widgetFormProps)
  return widgetFormProps
}

const castDefaultCommonErrorMessages = (errorResponse?: InternalErrorResponse): WidgetError => {
  const commonServerErrors = errorResponse?.errors?.common
  if (commonServerErrors) {
    return commonServerErrors
  }
  if (errorResponse?.message) {
    return [errorResponse.message]
  }
  return [translateService.t('common.somethingWentWrong')]
}
